import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { loadState } from "../../stores/store";
import {
  searchContestants,
  deleteContestant,
  updateCreatedByWithContestantCode,
  createContestant,
  updateContestantRound,
  updateContestantProvince
} from "../../api/contestant";
import { getSoloEvents } from "../../api/event";
import {
  createApplication,
  getApplicationsByContestant,
  deleteApplication,
  getApplicationsByContestants,
} from "../../api/application";
import { downloadCertificate } from "../../api/competition";
import { sendEligibilityNotification } from "../../api/user";
import { PlusOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import "./contestant.scss";
import {
  Button,
  Card,
  Typography,
  Modal,
  Spin,
  message,
  Flex,
  Space,
  Select,
  Input,
} from "antd";
import InvoiceDrawer from "../../components/Invoices/InvoiceDrawer/invoiceDrawer";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ContestantsTable from "../../components/Contestants/contestantTable";
import ApplyEventsModal from "../../components/Applications/applyEventsModal";
import CodeModal from "../../components/Contestants/codeModal";
import PhotoUpload from "../../components/frontendForm/photoUpload";
import { openNotificationWithIcon } from "../../utils/notificationUtils";
import { useSettings } from "../../contexts/settingsContext";
import MarkAllInvoicesPaidButton from "../../components/Invoices/multipleInvoiceButton";
import PaymentResultModal from "../../components/Invoices/paymentResultModal";
import ContestantApplicationsModal from "../../components/Contestants/contestantApplicationsModal";
import ContestantForm from "../../components/frontendForm/contestantForm";
import ContestantInfoDrawer from "../../components/Contestants/contestantInfoDrawer";
import { exportContestants } from "../../utils/exportContestantsUtils";
import { fetchProvinces } from "../../api/competition";

dayjs.extend(customParseFormat);
const { Title } = Typography;

const Contestants = () => {
  const [contestants, setContestants] = useState([]);
  const [selectedContestantForInvoice, setSelectedContestantForInvoice] =
    useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCodeModalVisible, setIsCodeModalVisible] = useState(false);
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false);
  const [isApplyEventsModalVisible, setIsApplyEventsModalVisible] =
    useState(false);
  const [applyEventModalLoading, setApplyEventModalLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isContestantFormModalVisible, setIsContestantFormModalVisible] =
    useState(false);
  const [isCreatingContestant, setIsCreatingContestant] = useState(false);
  const [contestantCode, setContestantCode] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedContestantForEvents, setSelectedContestantForEvents] =
    useState(null);
  const [initialSelectedEvents, setInitialSelectedEvents] = useState([]);
  const [applying, setApplying] = useState(false);
  const [selectedContestantApplications, setSelectedContestantApplications] =
    useState(null);
  const [selectedContestantInfo, setSelectedContestantInfo] = useState(null);
  const [photoUploadModalVisible, setPhotoUploadModalVisible] = useState(false);
  const [newlyCreatedContestant, setNewlyCreatedContestant] = useState(null);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [activeProvinces, setActiveProvinces] = useState([]);
  const [roundFilter, setRoundFilter] = useState(null);
  const [provinceFilter, setProvinceFilter] = useState("");
  const [applications, setApplications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalContestants, setTotalContestants] = useState(0);
  const [isExportingContestants, setIsExportingContestants] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [provinceSelectionModalVisible, setProvinceSelectionModalVisible] =
    useState(false);
  const [contestantForProvinceSelection, setContestantForProvinceSelection] =
    useState(null);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);
  const [updatingProvince, setUpdatingProvince] = useState(false);


  const { state: settings } = useSettings();
  const user = loadState().user;
  const userRole = user.role;

  const fetchData = useCallback(
    async (pageOverride, pageSizeOverride) => {
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;

      try {
        setLoading(true);
        const params = {
          page: pageToUse,
          pageSize: sizeToUse,
          filters: {
            round: roundFilter,
            province: provinceFilter,
          },
          searchText: searchQuery,
        };
        const result = await searchContestants(params);
        setContestants(result.data || []);
        setTotalContestants(result.total || 0);
      } catch (error) {
        console.error("Error fetching contestants:", error);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, roundFilter, provinceFilter, searchQuery]
  );

  useEffect(() => {
    fetchData(currentPage, pageSize);
  }, [fetchData, currentPage, pageSize]);

  const handleRowClick = useCallback(
    (contestantId) => {
      const contestant = contestants.find((c) => c._id === contestantId);
      setSelectedContestantForInvoice(contestant || null);
    },
    [contestants]
  );

  const loadProvinces = useCallback(async () => {
    try {
      const competitionId =
        settings?.selectedCompetition?._id || settings?.selectedCompetitionId;

      if (!competitionId) {
        setProvinces([]);
        setActiveProvinces([]);
        return;
      }

      const data = await fetchProvinces(competitionId);

      setProvinces(data || []);

      const now = new Date();
      const active = (data || []).filter((prov) => {
        if (!prov.active) return false;
        if (!prov.date) return true;
        const d = new Date(prov.date);
        return d >= now;
      });

      setActiveProvinces(active);
    } catch (error) {
      console.error("Error loading provinces", error);
      setProvinces([]);
      setActiveProvinces([]);
    }
  }, [settings]);

  useEffect(() => {
    loadProvinces();
  }, [loadProvinces]);

  const selectableProvinces = useMemo(() => {
    if (userRole === "admin") {
      return provinces || [];
    }
    return activeProvinces || [];
  }, [userRole, provinces, activeProvinces]);

  const handleDelete = async (contestantId) => {
    try {
      await deleteContestant(contestantId);

      setContestants((prev) =>
        prev.filter((contestant) => contestant._id !== contestantId)
      );
      setTotalContestants((prev) => Math.max(prev - 1, 0));

      openNotificationWithIcon(
        "success",
        "Deleted",
        "The contestant has been successfully deleted."
      );
    } catch (error) {
      console.error("Error deleting contestant:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        "There was an error deleting the contestant."
      );
    }
  };

  const handleApplyToEvents = async (contestant) => {
  try {
    setApplyEventModalLoading(true);

    const [applicationsData, eventsData] = await Promise.all([
      getApplicationsByContestant(contestant._id),
      getSoloEvents(),
    ]);

    const selectedEventsForRound = applicationsData
      .filter(
        (application) =>
          application.contestant &&
          !application.group &&
          application.round === contestant.round
      )
      .map((application) => application.event);

    setEvents(eventsData || []);
    setApplications(applicationsData);
    setSelectedContestantForEvents(contestant);
    setSelectedEvents(selectedEventsForRound || []);
    setInitialSelectedEvents(selectedEventsForRound || []);
    setIsApplyEventsModalVisible(true);
  } catch (error) {
    console.error("Error fetching applications/events for contestant:", error);
    openNotificationWithIcon(
      "error",
      "Error",
      "There was an error fetching applications or events for the contestant."
    );
  } finally {
    setApplyEventModalLoading(false);
  }
};


  const handlePassContestant = async (contestant) => {
    try {
      const result = await updateContestantRound(
        contestant._id,
        settings.selectedRound
      );
      message.success(result.message);

      // Keep UI in sync for the current page
      setContestants((prevContestants) =>
        prevContestants.map((c) =>
          c._id === contestant._id ? { ...c, round: c.round + 1 } : c
        )
      );
    } catch (e) {
      message.error(e.message);
    }

    const name = `${contestant.first_name} ${contestant.last_name}`;
    if (settings.selectedRound === 1) {
      await sendEligibilityNotification(name, contestant.email);
    }
  };

  const handleResendEligibility = async (contestant) => {
  const name = `${contestant.first_name} ${contestant.last_name}`;

  try {
    await sendEligibilityNotification(name, contestant.email);
    message.success("Eligibility notification re-sent.");
  } catch (error) {
    console.error("Error re-sending eligibility notification:", error);
    message.error(
      error.message || "Failed to re-send eligibility notification."
    );
  }
};

  const fetchAllContestantsForExport = useCallback(async () => {
    const exportPageSize = 100;
    let page = 1;
    let total = 0;
    const allContestants = [];

    while (true) {
      const result = await searchContestants({
        page,
        pageSize: exportPageSize,
        filters: {
          round: roundFilter,
          province: provinceFilter,
        },
        searchText: searchQuery,
      });

      const batch = result?.data || [];
      if (page === 1) {
        total = Number(result?.total) || 0;
      }

      allContestants.push(...batch);

      if (batch.length === 0) break;
      if (total > 0 && allContestants.length >= total) break;
      if (batch.length < exportPageSize) break;

      page += 1;
    }

    return allContestants;
  }, [roundFilter, provinceFilter, searchQuery]);

  const handleExportContestants = async () => {
    try {
      setIsExportingContestants(true);
      const allContestants = await fetchAllContestantsForExport();
      const contestantsWithProvince = allContestants.filter(
        (contestant) => contestant?.province
      );

      if (!contestantsWithProvince.length) {
        message.warning(
          "No solo entries with an audition date/province were found for the selected filters."
        );
        return;
      }

      await exportContestants(contestantsWithProvince, user.activeProvince);
    } catch (error) {
      console.error("Error exporting contestants:", error);
      message.error("Failed to export solo entries. Please try again.");
    } finally {
      setIsExportingContestants(false);
    }
  };

  const onSelectEvent = (event) => {
    setSelectedEvents((prevSelectedEvents) => {
      const isSelected = prevSelectedEvents.some((e) => e._id === event._id);
      if (isSelected) {
        return prevSelectedEvents.filter((e) => e._id !== event._id);
      }
      return [...prevSelectedEvents, event];
    });
  };

  const paginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize: pageSize,
      total: totalContestants,
      onChange: (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
      },
    }),
    [currentPage, pageSize, totalContestants]
  );

  const handleApplyEvents = async () => {
    try {
      setApplying(true);

      if (!selectedContestantForEvents) {
        throw new Error("No contestant selected");
      }

      if (
        selectedContestantForEvents.round === 1 &&
        (!selectedEvents || selectedEvents.length < 4)
      ) {
        openNotificationWithIcon(
          "warning",
          "Minimum Selection Required",
          "You must select at least 4 events for contestants in Round 1."
        );
        setApplying(false);
        return;
      }

      const applicationsForContestant = await getApplicationsByContestant(
        selectedContestantForEvents._id
      );
      const soloApplications = applicationsForContestant.filter(
        (app) =>
          app.contestant &&
          !app.group &&
          app.round === selectedContestantForEvents.round
      );
      const appliedEventIds = soloApplications.map((app) => app.event._id);

      const newEvents = selectedEvents.filter(
        (event) => !appliedEventIds.includes(event._id)
      );

      const applicationPromises = newEvents.map((event) =>
        createApplication({
          event: event._id,
          contestant: selectedContestantForEvents._id,
          created_by: user._id,
          province: selectedContestantForEvents.province,
          round: selectedContestantForEvents.round,
          instrument: event.instrument || undefined,
        })
      );

      await Promise.all(applicationPromises);

      const applicationsToDelete = soloApplications.filter(
        (app) => !selectedEvents.some((e) => e._id === app.event._id)
      );
      for (const app of applicationsToDelete) {
        await deleteApplication(app._id);
      }

      fetchData();
      setIsApplyEventsModalVisible(false);
      openNotificationWithIcon(
        "success",
        "Application Created",
        "The contestant has been successfully applied to the selected events."
      );
    } catch (error) {
      console.error("Error applying to events", error);
      openNotificationWithIcon("error", "Error", `${error.message}`);
    } finally {
      setApplying(false);
    }
  };

  const handleSubmitCode = async () => {
    try {
      await updateCreatedByWithContestantCode(contestantCode);
      setIsCodeModalVisible(false);
      openNotificationWithIcon(
        "success",
        "Updated",
        "The contestant has been successfully updated."
      );
      fetchData();
    } catch (error) {
      console.error("Error updating contestant:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        "There was an error updating the contestant."
      );
    }
  };

  const handleContestantApplicationsViewClick = (contestant) => {
    setSelectedContestantApplications(contestant);
  };

  const handleContestantViewClick = async (contestant) => {
    setSelectedContestantInfo(contestant);
  };

  // Called when a contestant is updated inside the drawer — update list and selected item
  const handleContestantUpdated = (updated) => {
    if (!updated) return;
    const data = updated.contestant ? updated.contestant : updated;
    setContestants((prev) => prev.map((c) => (c._id === data._id ? data : c)));
    setSelectedContestantInfo(data);
  };

  const handleContestantFormModalToggle = () => {
    setIsContestantFormModalVisible((prev) => !prev);
  };

  const handleCreateContestant = async (values) => {
    setIsCreatingContestant(true);
    try {
      const formattedValues = {
        first_name: values.first_name,
        last_name: values.last_name,
        contestant_number: values.contestant_number || undefined,
        address: {
          street_1: values["address.street_1"],
          street_2: values["address.street_2"],
          city: values["address.city"],
          country: values["address.country"],
          province: values["address.province"],
          postal_code: values["address.postal_code"],
        },
        gender: values.gender,
        id_number: values.id_number || "0",
        date_of_birth: values.date_of_birth
          ? dayjs(values.date_of_birth).format("YYYY-MM-DD")
          : null,
        passport_number: values.passport_number || "0",
        age: values.age,
        cell_number: values.cell_number,
        work_number: values.work_number,
        email: values.email,
        ice_contact_name: values.ice_contact_name,
        relationship_to_ice: values.relationship_to_ice,
        ice_contact_email: values.ice_contact_email,
        allergies: values.allergies || false,
        allergies_specification: values.allergies_specification,
        password: values.password,
        confirm_password: values.confirm_password,
        created_by: user._id,
        guardian: user._id,
        province: values.audition_province,
      };

      const newContestant = await createContestant(formattedValues);

      setNewlyCreatedContestant(newContestant.contestant);
      setIsCreatingContestant(false);
      setIsContestantFormModalVisible(false);
      setPhotoUploadModalVisible(true);
      setPhotoUploaded(false);
      fetchData();
      openNotificationWithIcon(
        "success",
        "Created",
        "The contestant has been successfully created."
      );
    } catch (error) {
      console.error("Error creating contestant:", error);
      setIsCreatingContestant(false);
      openNotificationWithIcon(
        "error",
        "Error",
        "There was an error creating the contestant."
      );
    }
  };

    const handleOpenProvinceSelectionModal = (contestant) => {
    setContestantForProvinceSelection(contestant);
    // preselect if they already have a province (just in case)
    const existingProvinceId =
      contestant.province && contestant.province._id
        ? contestant.province._id
        : contestant.province || null;
    setSelectedProvinceId(existingProvinceId);
    setProvinceSelectionModalVisible(true);
  };

  const handleProvinceSelectionCancel = () => {
    setProvinceSelectionModalVisible(false);
    setContestantForProvinceSelection(null);
    setSelectedProvinceId(null);
  };

  const handleProvinceSelectionConfirm = async () => {
    if (!contestantForProvinceSelection || !selectedProvinceId) {
      message.warning("Please select a province first.");
      return;
    }

    try {
      setUpdatingProvince(true);

      // call your API that updates contestant.province and their applications
      await updateContestantProvince(
        contestantForProvinceSelection._id,
        selectedProvinceId
      );

      // Update local state so UI reflects new province
      setContestants((prev) =>
        prev.map((c) =>
          c._id === contestantForProvinceSelection._id
            ? {
                ...c,
                province:
                  c.province && typeof c.province === "object"
                    ? { ...c.province, _id: selectedProvinceId }
                    : selectedProvinceId,
              }
            : c
        )
      );

      openNotificationWithIcon(
        "success",
        "Province Updated",
        "The contestant's province has been updated."
      );

      handleProvinceSelectionCancel();
      // reload page data if you want everything fully in sync:
      fetchData();
    } catch (error) {
      console.error("Error updating contestant province:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        "There was an error updating the contestant's province."
      );
    } finally {
      setUpdatingProvince(false);
    }
  };

  return (
    <div>
      <Flex
        style={{ marginBottom: "15px" }}
        className="dashboard_page_header_area"
        align="center"
        justify="space-between"
      >
        <Title
          className="dashboard_page_title"
          level={4}
          style={{ margin: 0 }}
        >
          Solo Entries
        </Title>
        <Space className="dashboard_page_title_actions" wrap>
          {userRole === "guardian" && (
            <Button
              type="primary"
              onClick={handleContestantFormModalToggle}
            >
              <PlusOutlined />
              Add Solo
            </Button>
          )}

          {userRole === "admin" && (
            <>
              <Button
                type="primary"
                onClick={handleExportContestants}
                loading={isExportingContestants}
              >
                Export Solo Entries
              </Button>
              <Button
                type="primary"
                onClick={handleContestantFormModalToggle}
              >
                Add Solo
              </Button>
            </>
          )}
        </Space>
      </Flex>

      <Space style={{ marginBottom: 16 }} wrap>
        <span>Filter by Round:</span>
        <Select
          value={roundFilter !== null ? roundFilter : ""}
          onChange={(value) => {
            setCurrentPage(1);
            setRoundFilter(value === "" ? null : Number(value));
          }}
          style={{ width: 120, marginRight: 16 }}
          placeholder="Select round"
        >
          <Select.Option value="">All</Select.Option>
          <Select.Option value={1}>1</Select.Option>
          <Select.Option value={2}>2</Select.Option>
          <Select.Option value={3}>3</Select.Option>
        </Select>

        <span>Filter by Audition Date:</span>
        <Select
          value={provinceFilter}
          onChange={(value) => {
            setCurrentPage(1);
            setProvinceFilter(value || "");
          }}
          style={{ width: 250 }}
          showSearch
          allowClear
          placeholder="Select a province"
        >
          <Select.Option value="">All</Select.Option>
          {provinces &&
            Array.isArray(provinces) &&
            provinces.map((prov) => (
              <Select.Option key={prov._id} value={prov._id}>
                {prov.province} ({prov.location}) - {prov.date}
              </Select.Option>
            ))}
        </Select>
      </Space>

      <Card bordered={false}>
        <Input
          placeholder="Search by first name, last name or email"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setCurrentPage(1);
              setSearchQuery(searchText);
            }
          }}
          style={{ marginBottom: 16 }}
          suffix={
            <>
              <SearchOutlined style={{ marginRight: "8px" }} />
              {searchText && (
                <CloseOutlined
                  onClick={() => {
                    setSearchText("");
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  style={{ cursor: "pointer" }}
                />
              )}
            </>
          }
        />

      <ContestantsTable
        loading={loading}
        contestants={contestants}
        handleRowClick={handleRowClick}
        handleDelete={handleDelete}
        handleApplyToEvents={handleApplyToEvents}
        handleContestantApplicationsViewClick={handleContestantApplicationsViewClick}
        handleContestantViewClick={handleContestantViewClick}
        handlePassContestant={handlePassContestant}
        handleResendEligibility={handleResendEligibility}
        settings={settings}
        userRole={userRole}
        pagination={paginationConfig}
        handleSelectProvince={handleOpenProvinceSelectionModal}
      />

      </Card>

      {selectedContestantForInvoice && (
        <InvoiceDrawer
          contestant={selectedContestantForInvoice}
          source="contestant"
          onRequestClose={() => setSelectedContestantForInvoice(null)}
          fetchData={fetchData}
        />
      )}

      <CodeModal
        isVisible={isCodeModalVisible}
        contestant_code={contestantCode}
        handleCodeChange={(e) => setContestantCode(e.target.value)}
        handleModalToggle={() => setIsCodeModalVisible(false)}
        handleSubmitCode={handleSubmitCode}
      />

      <ApplyEventsModal
        visible={applyEventModalLoading || isApplyEventsModalVisible}
        onClose={() => setIsApplyEventsModalVisible(false)}
        events={events}
        loading={applyEventModalLoading}
        userRole={userRole}
        handleDeleteEvent={() => {}}
        onSelectEvent={onSelectEvent}
        selectedEvents={selectedEvents}
        onApply={handleApplyEvents}
        confirmLoading={applying}
        contestant={selectedContestantForEvents}
        applications={applications}
      />

      {selectedContestantApplications && (
        <ContestantApplicationsModal
          visible={!!selectedContestantApplications}
          contestant={selectedContestantApplications}
          onCancel={() => setSelectedContestantApplications(null)}
        />
      )}

      {selectedContestantInfo && settings.selectedCompetition && (
        <ContestantInfoDrawer
          contestant={selectedContestantInfo}
          currentUser={user}
          competitionId={settings.selectedCompetition._id}
          onClose={() => setSelectedContestantInfo(null)}
          onDataUpdated={handleContestantUpdated}
        />
      )}

      <Modal
        title="Add New Contestant"
        visible={isContestantFormModalVisible}
        footer={null}
        onCancel={handleContestantFormModalToggle}
        width={800}
      >
        {isCreatingContestant ? (
          <div style={{ textAlign: "center" }}>
            <Spin size="large" />
            <div>Creating contestant...</div>
          </div>
        ) : (
          <ContestantForm
            onNext={handleCreateContestant}
            user={user}
            enforceAgeRestriction={false}
            provinces={selectableProvinces}
          />
        )}
      </Modal>
      
      <Modal
        title="Select Province"
        visible={provinceSelectionModalVisible}
        onOk={handleProvinceSelectionConfirm}
        confirmLoading={updatingProvince}
        onCancel={handleProvinceSelectionCancel}
        destroyOnClose
      >
        <p>
          Please select a province for{" "}
          {contestantForProvinceSelection
            ? `${contestantForProvinceSelection.first_name} ${contestantForProvinceSelection.last_name}`
            : ""}
          .
        </p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select a province"
          value={selectedProvinceId || undefined}
          onChange={(value) => setSelectedProvinceId(value)}
          showSearch
          optionFilterProp="children"
        >
          {selectableProvinces &&
            Array.isArray(selectableProvinces) &&
            selectableProvinces.map((prov) => (
              <Select.Option key={prov._id} value={prov._id}>
                {prov.province} ({prov.location}) - {prov.date}
              </Select.Option>
            ))}
        </Select>
      </Modal>

      <Modal
        title="Upload Profile Photo"
        visible={photoUploadModalVisible}
        maskClosable={false}
        keyboard={false}
        closable={false}
        footer={null}
        destroyOnClose
        width={400}
      >
        <PhotoUpload
          contestant={newlyCreatedContestant}
          setContestant={setNewlyCreatedContestant}
          onPhotoUploaded={() => {
            setPhotoUploaded(true);
            setTimeout(() => {
              setPhotoUploadModalVisible(false);
              setNewlyCreatedContestant(null);
            }, 800);
          }}
          submitting={false}
        />
      </Modal>
    </div>
  );
};

export default Contestants;
